/**
 * useHero — the hero picture and words for a public module, as set in
 * Admin → Hero images. Falls back to what the app ships with.
 */
import { useQuery } from '@tanstack/react-query';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useHeroes = () => useQuery({
  queryKey: ['public-heroes'],
  queryFn: () => fetch(`${API_URL}/api/public/heroes`)
    .then((r) => r.json()).then((d) => d.heroes || {}).catch(() => ({})),
  staleTime: 300000,
});

export const useHero = (key, fallback = {}) => {
  const { data } = useHeroes();
  const hero = (data || {})[key] || {};
  const abs = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url || '');
  const image = hero.image_url || fallback.image_url || '';
  return {
    image_url: abs(image),
    // iter 636 — what to show when the configured picture will not load. An
    // uploaded banner lives in object storage; when that is unreachable the
    // section used to render an empty box, which reads as a broken page rather
    // than a missing image. `onImageError` swaps in the art the app ships with.
    fallback_image_url: abs(fallback.image_url || ''),
    onImageError: (event) => {
      const shipped = abs(fallback.image_url || '');
      if (shipped && event?.currentTarget && event.currentTarget.src !== shipped) {
        event.currentTarget.src = shipped;
      } else if (event?.currentTarget) {
        event.currentTarget.style.visibility = 'hidden';
      }
    },
    video_url: abs(hero.video_url || fallback.video_url || ''),
    media_priority: hero.media_priority || 'image',
    eyebrow: hero.eyebrow ?? fallback.eyebrow ?? '',
    title: hero.title ?? fallback.title ?? '',
    accent: hero.accent ?? fallback.accent ?? '',
    blurb: hero.blurb ?? fallback.blurb ?? '',
  };
};

export default useHero;
